'use client';

import Slider from 'react-slick';
import type { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import type { LoginShowcaseSlide } from '@attik/types/loginShowcase.types';

/** Same-origin via the /api/proxy rewrite, so the browser only ever sees our domain. */
const imageSrc = (id: string) => `/api/proxy/login-showcase/image/${id}`;

const sliderSettings: Settings = {
  arrows: false,
  dots: false,
  fade: true,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 6000,
  speed: 1200,
  pauseOnHover: false,
  swipe: false,
};

/** Recent inspections slider for the login page's right panel. */
export function LoginShowcase({ slides }: { slides: LoginShowcaseSlide[] }) {
  if (!slides.length) return <FallbackPhoto />;

  return (
    <Slider {...sliderSettings} className='login-showcase h-full [&_.slick-list]:h-full [&_.slick-slide>div]:h-full [&_.slick-track]:h-full'>
      {slides.map((slide) => (
        <div key={slide.photoId} className='relative h-full'>
          {/* Plain img: the backend already resizes and re-encodes these. */}
          <img
            src={imageSrc(slide.photoId)}
            alt={`Recent inspection by ${slide.companyName}`}
            className='absolute inset-0 h-full w-full object-cover'
          />
          <div className='absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-16 text-white lg:p-6 lg:pt-24'>
            <div className='flex min-w-0 items-center gap-3'>
              {slide.inspectorImageId ? (
                <img
                  src={imageSrc(slide.inspectorImageId)}
                  alt=''
                  className='h-9 w-9 shrink-0 rounded-full lg:h-11 lg:w-11 border-2 border-white/80 object-cover'
                />
              ) : (
                <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full lg:h-11 lg:w-11 border-2 border-white/80 bg-white/20 font-bold'>
                  {slide.inspectorName.charAt(0)}
                </div>
              )}
              <div className='min-w-0'>
                <p className='text-[11px] uppercase tracking-wider text-white/70 lg:text-xs'>
                  Inspected this week by
                </p>
                <p className='truncate font-semibold'>{slide.inspectorName}</p>
              </div>
            </div>
            <div className='flex min-w-0 items-center gap-3'>
              <p className='hidden truncate text-sm font-medium text-white/90 lg:block'>
                {slide.companyName}
              </p>
              {slide.companyLogoId && (
                <img
                  src={imageSrc(slide.companyLogoId)}
                  alt={slide.companyName}
                  className='h-9 w-9 shrink-0 rounded-lg lg:h-11 lg:w-11 bg-white object-contain p-1'
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </Slider>
  );
}

/** Positioned like the slides so its natural size (1200x1200) never sizes the layout. */
function FallbackPhoto() {
  return (
    <div className='relative h-full'>
      <img
        src='/loginPic.jpg'
        alt='Home in Colorado'
        className='absolute inset-0 h-full w-full object-cover'
      />
    </div>
  );
}
